import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AvisoTaxaCartao } from '../componentes/AvisoTaxaCartao.tsx';
import { Marca } from '../componentes/Marca.tsx';
import { BotaoTema } from '../componentes/Tema.tsx';
import {
  Aviso,
  Botao,
  Campo,
  CampoArea,
  Carregando,
  Cartao,
  Dado,
  Escolha,
  Icone,
  Opcoes,
  Select,
  TextoFormatado,
} from '../componentes/ui/index.ts';
import {
  api,
  cpfValido,
  data,
  ErroApi,
  mascararCpf,
  mascararTelefone,
  moeda,
} from '../lib/api.ts';
import { remover, salvar } from '../lib/rascunhos.ts';
import type { ConfigPublica, InscricaoRascunho } from '../lib/tipos.ts';

const PASSOS = [
  'Consentimento',
  'Adolescente',
  'Quarto',
  'Responsável',
  'Saúde',
  'Revisão',
] as const;

const SIM_NAO = [
  { valor: 'sim', rotulo: 'Sim' },
  { valor: 'nao', rotulo: 'Não' },
] as const;

const TAMANHOS = ['PP', 'P', 'M', 'G', 'GG', 'XG'].map((t) => ({ valor: t, rotulo: t }));

const SEXOS = [
  { valor: 'feminino', rotulo: 'Feminino' },
  { valor: 'masculino', rotulo: 'Masculino' },
] as const;

type Rascunho = {
  nomeResponsavel: string;
  telefoneResponsavel: string;
  telefoneAdolescente: string;
  house: string;
  dados: Record<string, string>;
  saude: Record<string, string>;
};

const VAZIO: Rascunho = {
  nomeResponsavel: '',
  telefoneResponsavel: '',
  telefoneAdolescente: '',
  house: '',
  dados: {},
  saude: {},
};

/** Só dígitos, para validar CPF e telefone sem depender da máscara. */
const digitos = (v: string) => v.replace(/\D/g, '');
const preenchido = (v: string | undefined) => (v ?? '').trim() !== '';

export function Formulario() {
  const { token = '' } = useParams();
  const navegar = useNavigate();
  const location = useLocation();
  const estadoInicial = location.state as { aviso?: string; saudeOmitida?: boolean } | null;

  const [inscricao, setInscricao] = useState<InscricaoRascunho | null>(null);
  const [config, setConfig] = useState<ConfigPublica | null>(null);
  const [form, setForm] = useState<Rascunho>(VAZIO);
  const [passo, setPasso] = useState(1);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [avisoSaude, setAvisoSaude] = useState(estadoInicial?.aviso ?? null);

  useEffect(() => {
    api<ConfigPublica>('/api/inscricoes/config')
      .then(setConfig)
      .catch(() => {});
  }, []);

  useEffect(() => {
    api<{ inscricao: InscricaoRascunho }>(`/api/inscricoes/${token}`)
      .then(({ inscricao }) => {
        if (inscricao.status === 'submitted') {
          navegar(`/confirmacao/${inscricao.protocolo}`, { replace: true });
          return;
        }
        setInscricao(inscricao);
        setPasso(inscricao.step);
        setForm({
          nomeResponsavel: inscricao.nomeResponsavel ?? '',
          telefoneResponsavel: mascararTelefone(inscricao.telefoneResponsavel ?? ''),
          telefoneAdolescente: mascararTelefone(inscricao.telefoneAdolescente ?? ''),
          house: inscricao.house ?? '',
          dados: (inscricao.dados as Record<string, string>) ?? {},
          saude: (inscricao.saude as Record<string, string>) ?? {},
        });
      })
      .catch((e: ErroApi) => setErro(e.mensagem))
      .finally(() => setCarregando(false));
  }, [token, navegar]);

  const corpoAtual = useCallback(
    (passoAtual: number) => ({
      step: passoAtual,
      nomeResponsavel: form.nomeResponsavel,
      telefoneResponsavel: digitos(form.telefoneResponsavel),
      telefoneAdolescente: digitos(form.telefoneAdolescente),
      house: form.house,
      dados: form.dados,
      saude: form.saude,
    }),
    [form],
  );

  const persistir = useCallback(
    async (passoAtual: number) => {
      setSalvando(true);
      try {
        await api(`/api/inscricoes/${token}`, { metodo: 'PATCH', corpo: corpoAtual(passoAtual) });
        if (inscricao) {
          salvar({
            token,
            email: inscricao.email,
            nomeAdolescente: inscricao.nomeAdolescente,
            step: passoAtual,
            updatedAt: new Date().toISOString(),
          });
        }
      } catch {
        // Autosave falho não interrompe o preenchimento: o conteúdo continua na
        // tela e a próxima gravação tenta de novo.
      } finally {
        setSalvando(false);
      }
    },
    [token, corpoAtual, inscricao],
  );

  // Autosave com debounce de 2s dentro do passo.
  const primeiraRenderizacao = useRef(true);
  useEffect(() => {
    if (carregando) return;
    if (primeiraRenderizacao.current) {
      primeiraRenderizacao.current = false;
      return;
    }
    const t = setTimeout(() => void persistir(passo), 2000);
    return () => clearTimeout(t);
  }, [form, passo, carregando, persistir]);

  function definir(chave: keyof Rascunho, valor: string) {
    setForm((f) => ({ ...f, [chave]: valor }));
  }
  function definirDados(chave: string, valor: string) {
    setForm((f) => ({ ...f, dados: { ...f.dados, [chave]: valor } }));
  }
  function definirSaude(chave: string, valor: string) {
    setForm((f) => ({ ...f, saude: { ...f.saude, [chave]: valor } }));
  }

  /**
   * Troca a resposta de uma pergunta-gatilho e limpa o detalhe quando a
   * resposta deixa de ser "sim".
   *
   * Sem isto, quem responde "sim", escreve a descrição e depois volta para
   * "não" deixa a descrição gravada — some da tela, mas continua no banco e
   * chega para a liderança como se ainda valesse.
   */
  function definirGatilho(
    onde: 'dados' | 'saude',
    chave: string,
    valor: string,
    dependentes: string[],
  ) {
    setForm((f) => {
      const grupo = { ...f[onde], [chave]: valor };
      if (valor !== 'sim') for (const d of dependentes) delete grupo[d];
      return { ...f, [onde]: grupo };
    });
  }

  async function avancar() {
    const proximo = Math.min(passo + 1, PASSOS.length);
    setPasso(proximo);
    await persistir(proximo);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function voltar() {
    setPasso((p) => Math.max(1, p - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function enviar() {
    setEnviando(true);
    setErro(null);
    try {
      await persistir(passo);
      const r = await api<{ protocolo: string; valorTotal: string }>(
        `/api/inscricoes/${token}/enviar`,
        { metodo: 'POST', corpo: {} },
      );
      remover(token);
      navegar(`/confirmacao/${r.protocolo}`, {
        replace: true,
        state: { nomeAdolescente: inscricao?.nomeAdolescente, valorTotal: r.valorTotal },
      });
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao enviar');
      setEnviando(false);
    }
  }

  if (carregando) return <Carregando texto="Abrindo sua ficha" />;

  if (!inscricao) {
    return (
      <div className="container container-form pilha" style={{ paddingBlock: 'var(--gap-7)' }}>
        <Aviso tipo="erro">{erro ?? 'Rascunho não encontrado.'}</Aviso>
        <Botao variante="vazado" onClick={() => navegar('/')} icone={<Icone.Voltar />}>
          Voltar ao início
        </Botao>
      </div>
    );
  }

  const { dados, saude } = form;
  const telAdolescenteOk = digitos(form.telefoneAdolescente).length === 11;
  const telResponsavelOk = digitos(form.telefoneResponsavel).length === 11;
  const cpfAdolescenteOk = cpfValido(dados.cpfAdolescente ?? '');
  const cpfResponsavelOk = cpfValido(dados.cpfResponsavel ?? '');

  /**
   * Um booleano por passo. As perguntas condicionais entram na conta só
   * quando a pergunta-gatilho está em "sim" — é o mesmo desenho do "pular
   * para a pergunta X" do formulário original, resolvido mostrando ou
   * escondendo no lugar de trocar de página.
   */
  const podeAvancar = {
    1: dados.consentimentoDados === 'sim',
    2:
      preenchido(dados.nascimento) &&
      preenchido(dados.sexo) &&
      telAdolescenteOk &&
      cpfAdolescenteOk &&
      digitos(dados.rgAdolescente ?? '').length >= 5,
    3:
      preenchido(form.house) &&
      preenchido(dados.divideQuarto) &&
      (dados.divideQuarto !== 'sim' ||
        (preenchido(dados.divideCom) && preenchido(dados.divideMotivo))),
    4:
      form.nomeResponsavel.trim().length > 2 &&
      preenchido(dados.parentesco) &&
      cpfResponsavelOk &&
      telResponsavelOk,
    5:
      preenchido(saude.temPlano) &&
      (saude.temPlano !== 'sim' || preenchido(saude.planoOperadora)) &&
      preenchido(saude.temAlergia) &&
      (saude.temAlergia !== 'sim' || preenchido(saude.alergiaDescricao)) &&
      preenchido(saude.temMedicacaoContinua) &&
      (saude.temMedicacaoContinua !== 'sim' || preenchido(saude.medicacaoDescricao)),
    6: true,
  }[passo as 1 | 2 | 3 | 4 | 5 | 6];

  // O valor só é travado no envio (rota /enviar); até lá, o que vale é o
  // padrão vigente na configuração — a não ser que o admin já tenha ajustado
  // esta ficha manualmente. Comparação numérica: o Postgres devolve "0.00",
  // nunca "0".
  const bruto = Number(inscricao.valorTotal) > 0 ? inscricao.valorTotal : config?.valorPadrao;
  const valorExibido = bruto && Number(bruto) > 0 ? bruto : null;

  return (
    <div className="container container-form" style={{ paddingBlock: 'var(--gap-6)' }}>
      <header className="linha linha-entre" style={{ marginBottom: 'var(--gap-5)' }}>
        <Marca subtitulo={inscricao.nomeAdolescente} />
        <BotaoTema />
      </header>

      <nav aria-label="Progresso" className="pilha-2" style={{ marginBottom: 'var(--gap-5)' }}>
        <div className="progresso">
          {PASSOS.map((nome, i) => (
            <div
              key={nome}
              title={nome}
              className={`progresso__passo${
                i + 1 === passo
                  ? ' progresso__passo--atual'
                  : i + 1 < passo
                    ? ' progresso__passo--feito'
                    : ''
              }`}
            />
          ))}
        </div>
        <div className="linha linha-entre">
          <span className="texto-fraco">
            Passo {passo} de {PASSOS.length} · <strong>{PASSOS[passo - 1]}</strong>
          </span>
          {/* aria-live discreto: avisa que salvou sem interromper a digitação. */}
          <span className="texto-fraco linha" style={{ gap: 'var(--gap-1)' }} aria-live="polite">
            {salvando ? (
              <>
                <span className="girador" /> salvando
              </>
            ) : (
              <>
                <Icone.Check tamanho={13} /> salvo
              </>
            )}
          </span>
        </div>
      </nav>

      <main id="conteudo">
        <Cartao>
          {passo === 1 && (
            <div className="pilha">
              <h2>Antes de começar</h2>

              {(config?.dataAcampamento || config?.local || valorExibido) && (
                <div className="pilha-3 cartao" style={{ background: 'var(--superficie-alta)' }}>
                  {config?.dataAcampamento && (
                    <Dado titulo="Data">{data(config.dataAcampamento)}</Dado>
                  )}
                  {config?.local && <Dado titulo="Local">{config.local}</Dado>}
                  {valorExibido && <Dado titulo="Valor">{moeda(valorExibido)}</Dado>}
                </div>
              )}

              <p className="texto-suave">
                Respeitamos sua privacidade. Ao continuar, você autoriza o uso dos dados
                exclusivamente para:
              </p>
              <ul className="texto-suave texto-sm" style={{ paddingLeft: 'var(--gap-5)' }}>
                <li>identificar acampantes e responsáveis;</li>
                <li>comunicação com acampantes e responsáveis;</li>
                <li>
                  compartilhar nome, RG e CPF dos acampantes com a empresa de transporte;
                </li>
                <li>compartilhar o nome dos acampantes com o local do acampamento;</li>
                <li>
                  uso da imagem do(a) adolescente em redes sociais e/ou material de divulgação do
                  evento.
                </li>
              </ul>

              <Aviso tipo="alerta">
                Preencher a ficha não garante a vaga. A vaga é garantida com o pagamento.
              </Aviso>

              <Escolha
                marcada={dados.consentimentoDados === 'sim'}
                aoMudar={(v) => definirDados('consentimentoDados', v ? 'sim' : '')}
              >
                Entendi e quero continuar.
              </Escolha>
            </div>
          )}

          {passo === 2 && (
            <div className="pilha">
              <h2>Adolescente</h2>
              <p className="texto-fraco">Inscrição de {inscricao.nomeAdolescente}.</p>

              <Campo
                rotulo="Data de nascimento"
                type="date"
                value={dados.nascimento ?? ''}
                onChange={(e) => definirDados('nascimento', e.target.value)}
                obrigatorio
                dica="Aceitamos adolescentes que tenham entre 13 e 18 anos na data do acampamento."
              />

              <Opcoes
                rotulo="Sexo"
                opcoes={[...SEXOS]}
                valor={(dados.sexo as 'feminino' | 'masculino') ?? null}
                aoMudar={(v) => definirDados('sexo', v)}
                obrigatorio
                dica="Usado só para organizar os quartos."
              />

              <Campo
                rotulo="WhatsApp do adolescente"
                value={form.telefoneAdolescente}
                onChange={(e) => definir('telefoneAdolescente', mascararTelefone(e.target.value))}
                inputMode="numeric"
                placeholder="(11) 98765-4321"
                obrigatorio
                dica="Preferencialmente o número dele, não o seu."
                erro={
                  form.telefoneAdolescente && !telAdolescenteOk ? 'Informe DDD + 9 dígitos' : null
                }
              />

              <Campo
                rotulo="CPF do adolescente"
                value={dados.cpfAdolescente ?? ''}
                onChange={(e) => definirDados('cpfAdolescente', mascararCpf(e.target.value))}
                inputMode="numeric"
                placeholder="000.000.000-00"
                obrigatorio
                dica="Vai para a empresa de transporte."
                erro={dados.cpfAdolescente && !cpfAdolescenteOk ? 'CPF inválido' : null}
              />

              <Campo
                rotulo="RG ou CIN do adolescente"
                value={dados.rgAdolescente ?? ''}
                onChange={(e) => definirDados('rgAdolescente', e.target.value)}
                obrigatorio
                dica="Apenas os algarismos."
              />

              <Select
                rotulo="Tamanho da camiseta"
                opcoes={TAMANHOS}
                valor={dados.camiseta ?? null}
                aoMudar={(v) => definirDados('camiseta', v)}
                placeholder="Selecione o tamanho"
                opcional
              />
            </div>
          )}

          {passo === 3 && (
            <div className="pilha">
              <h2>House e quarto</h2>

              <Campo
                rotulo="Nome do(a) discipulador(a) do adolescente"
                value={form.house}
                onChange={(e) => definir('house', e.target.value)}
                obrigatorio
                dica='Se não frequenta nenhum house, escreva "não frequenta".'
              />

              <Opcoes
                rotulo="Precisa dividir quarto/mala com algum familiar ou amigo?"
                opcoes={[...SIM_NAO]}
                valor={(dados.divideQuarto as 'sim' | 'nao') ?? null}
                aoMudar={(v) =>
                  definirGatilho('dados', 'divideQuarto', v, ['divideCom', 'divideMotivo'])
                }
                obrigatorio
                dica="Permitido apenas entre adolescentes do mesmo sexo."
              />

              {dados.divideQuarto === 'sim' && (
                <>
                  <Campo
                    rotulo="Com qual adolescente vai dividir?"
                    value={dados.divideCom ?? ''}
                    onChange={(e) => definirDados('divideCom', e.target.value)}
                    obrigatorio
                    dica="Nome completo."
                  />
                  <CampoArea
                    rotulo="Qual o parentesco, ou o motivo?"
                    value={dados.divideMotivo ?? ''}
                    onChange={(e) => definirDados('divideMotivo', e.target.value)}
                    obrigatorio
                    dica='Se for da família, basta o grau — "irmão", "primo". Se for amigo, explique o motivo.'
                    maximo={300}
                  />
                </>
              )}
            </div>
          )}

          {passo === 4 && (
            <div className="pilha">
              <h2>Responsável</h2>
              <p className="texto-fraco">
                A autorização será enviada para {inscricao.email}.
              </p>

              <Campo
                rotulo="Seu nome completo"
                value={form.nomeResponsavel}
                onChange={(e) => definir('nomeResponsavel', e.target.value)}
                autoComplete="name"
                obrigatorio
              />

              <Campo
                rotulo="Grau de parentesco com o adolescente"
                value={dados.parentesco ?? ''}
                onChange={(e) => definirDados('parentesco', e.target.value)}
                obrigatorio
                dica='Ex.: "mãe", "pai", "avó", "tio".'
              />

              <Campo
                rotulo="Seu CPF"
                value={dados.cpfResponsavel ?? ''}
                onChange={(e) => definirDados('cpfResponsavel', mascararCpf(e.target.value))}
                inputMode="numeric"
                placeholder="000.000.000-00"
                obrigatorio
                erro={dados.cpfResponsavel && !cpfResponsavelOk ? 'CPF inválido' : null}
              />

              <Campo
                rotulo="Seu telefone/WhatsApp"
                value={form.telefoneResponsavel}
                onChange={(e) => definir('telefoneResponsavel', mascararTelefone(e.target.value))}
                inputMode="numeric"
                autoComplete="tel"
                placeholder="(11) 98765-4321"
                obrigatorio
                dica="É para onde a liderança liga numa emergência."
                erro={
                  form.telefoneResponsavel && !telResponsavelOk ? 'Informe DDD + 9 dígitos' : null
                }
              />
            </div>
          )}

          {passo === 5 && (
            <div className="pilha">
              <h2>Saúde</h2>

              {avisoSaude && (
                <Aviso tipo="alerta">
                  {avisoSaude}
                  <div style={{ marginTop: 'var(--gap-3)' }}>
                    <Botao variante="fantasma" tamanho="pequeno" onClick={() => setAvisoSaude(null)}>
                      Entendi
                    </Botao>
                  </div>
                </Aviso>
              )}

              <p className="texto-fraco">
                É o que a equipe usa numa emergência durante o acampamento.
              </p>

              <Opcoes
                rotulo="O adolescente tem plano de saúde?"
                opcoes={[...SIM_NAO]}
                valor={(saude.temPlano as 'sim' | 'nao') ?? null}
                aoMudar={(v) =>
                  definirGatilho('saude', 'temPlano', v, ['planoOperadora', 'planoCarteirinha'])
                }
                obrigatorio
              />

              {saude.temPlano === 'sim' && (
                <>
                  <Campo
                    rotulo="Nome, operadora e tipo do plano"
                    value={saude.planoOperadora ?? ''}
                    onChange={(e) => definirSaude('planoOperadora', e.target.value)}
                    obrigatorio
                    dica="Ex.: Amil 400 individual enfermaria."
                  />
                  <Campo
                    rotulo="Número da carteirinha"
                    value={saude.planoCarteirinha ?? ''}
                    onChange={(e) => definirSaude('planoCarteirinha', e.target.value)}
                    opcional
                  />
                </>
              )}

              <Opcoes
                rotulo="Tem alguma alergia ou restrição alimentar?"
                opcoes={[...SIM_NAO]}
                valor={(saude.temAlergia as 'sim' | 'nao') ?? null}
                aoMudar={(v) => definirGatilho('saude', 'temAlergia', v, ['alergiaDescricao'])}
                obrigatorio
              />

              {saude.temAlergia === 'sim' && (
                <CampoArea
                  rotulo="Descreva a alergia e/ou as restrições alimentares"
                  value={saude.alergiaDescricao ?? ''}
                  onChange={(e) => definirSaude('alergiaDescricao', e.target.value)}
                  obrigatorio
                  maximo={500}
                />
              )}

              <Opcoes
                rotulo="Usa algum medicamento de uso contínuo?"
                opcoes={[...SIM_NAO]}
                valor={(saude.temMedicacaoContinua as 'sim' | 'nao') ?? null}
                aoMudar={(v) =>
                  definirGatilho('saude', 'temMedicacaoContinua', v, ['medicacaoDescricao'])
                }
                obrigatorio
              />

              {saude.temMedicacaoContinua === 'sim' && (
                <CampoArea
                  rotulo="Qual o medicamento, a dosagem e o horário?"
                  value={saude.medicacaoDescricao ?? ''}
                  onChange={(e) => definirSaude('medicacaoDescricao', e.target.value)}
                  obrigatorio
                  dica="Inclua o que mais julgar importante."
                  maximo={500}
                />
              )}

              <CampoArea
                rotulo="Medicação não-contínua ou cuidados especiais"
                value={saude.cuidadosEspeciais ?? ''}
                onChange={(e) => definirSaude('cuidadosEspeciais', e.target.value)}
                opcional
                dica="Em caso de dor de cabeça, cólica, dor muscular ou enjoo, há algum medicamento que costumam dar? Comente também outros cuidados que devemos tomar."
                maximo={500}
              />
            </div>
          )}

          {passo === 6 && (
            <div className="pilha">
              <h2>Revisão</h2>

              <div className="cartao pilha-3" style={{ background: 'var(--superficie-alta)' }}>
                <Dado titulo="Adolescente">{inscricao.nomeAdolescente}</Dado>
                <Dado titulo="Nascimento">{dados.nascimento}</Dado>
                <Dado titulo="Sexo">{dados.sexo}</Dado>
                <Dado titulo="WhatsApp">{form.telefoneAdolescente}</Dado>
                <Dado titulo="CPF">{dados.cpfAdolescente}</Dado>
                <Dado titulo="RG/CIN">{dados.rgAdolescente}</Dado>
                <Dado titulo="Camiseta">{dados.camiseta}</Dado>
                <Dado titulo="House">{form.house}</Dado>
                <Dado titulo="Divide quarto">
                  {dados.divideQuarto === 'sim' ? `sim — ${dados.divideCom}` : 'não'}
                </Dado>
                <Dado titulo="Responsável">{form.nomeResponsavel}</Dado>
                <Dado titulo="Parentesco">{dados.parentesco}</Dado>
                <Dado titulo="Telefone">{form.telefoneResponsavel}</Dado>
                <Dado titulo="E-mail">{inscricao.email}</Dado>
                <Dado titulo="Plano de saúde">
                  {saude.temPlano === 'sim' ? saude.planoOperadora : 'não tem'}
                </Dado>
                <Dado titulo="Alergias">
                  {saude.temAlergia === 'sim' ? saude.alergiaDescricao : 'nenhuma'}
                </Dado>
                <Dado titulo="Medicação contínua">
                  {saude.temMedicacaoContinua === 'sim' ? saude.medicacaoDescricao : 'nenhuma'}
                </Dado>
                {valorExibido && <Dado titulo="Valor do acampamento">{moeda(valorExibido)}</Dado>}
              </div>

              {(config?.instrucoesPagamento || valorExibido) && (
                <Aviso tipo="info">
                  <div className="pilha-2">
                    {config?.instrucoesPagamento && (
                      <TextoFormatado texto={config.instrucoesPagamento} />
                    )}
                    {valorExibido && <AvisoTaxaCartao valor={valorExibido} />}
                  </div>
                </Aviso>
              )}

              <Escolha
                marcada={dados.consentimentoPagamento === 'sim'}
                aoMudar={(v) => definirDados('consentimentoPagamento', v ? 'sim' : '')}
              >
                Estou ciente das condições de pagamento e vou procurar a mesa de pagamento.
              </Escolha>

              <Escolha
                marcada={dados.autorizo === 'sim'}
                aoMudar={(v) => definirDados('autorizo', v ? 'sim' : '')}
              >
                Autorizo a participação de <strong>{inscricao.nomeAdolescente}</strong> no
                acampamento TeensIBP e confirmo que as informações acima estão corretas.
              </Escolha>

              {erro && <Aviso tipo="erro">{erro}</Aviso>}
            </div>
          )}

          <div className="linha linha-entre" style={{ marginTop: 'var(--gap-6)' }}>
            <Botao
              variante="fantasma"
              onClick={voltar}
              disabled={passo === 1 || enviando}
              icone={<Icone.Voltar />}
            >
              Voltar
            </Botao>

            {passo < PASSOS.length ? (
              <Botao
                onClick={() => void avancar()}
                disabled={!podeAvancar}
                iconeDireita={<Icone.Seta style={{ rotate: '-90deg' }} />}
              >
                Continuar
              </Botao>
            ) : (
              // Desabilitado durante o envio: é a mitigação que importa mais que
              // a constraint contra a duplicata acidental.
              <Botao
                onClick={() => void enviar()}
                carregando={enviando}
                disabled={
                  dados.autorizo !== 'sim' || dados.consentimentoPagamento !== 'sim'
                }
              >
                Enviar inscrição
              </Botao>
            )}
          </div>
        </Cartao>
      </main>
    </div>
  );
}
